import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useAdminOrders, useKitchenStations } from '../../lib/admin-hooks';
import type { ActionResult } from '../../lib/admin-hooks';
import { Skeleton } from '../../components/ui/Skeleton';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Clock, Check, AlertCircle, CreditCard, Banknote, Smartphone, Play, ChefHat, Truck, XCircle, X } from 'lucide-react';
import { cn } from '../../lib/utils';
// Global roles that can manage the full order lifecycle
const GLOBAL_ROLES = ['owner', 'admin', 'manager', 'caja'];
interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
}
export function KDSPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const [searchParams] = useSearchParams();
  const lockedStation = searchParams.get('station');
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  const {
    stations,
    loading: stationsLoading
  } = useKitchenStations(restaurant?.id);
  const {
    orders,
    loading: ordersLoading,
    acceptOrder,
    startOrderPreparation,
    markOrderReady,
    deliverOrder,
    cancelOrder,
    markItemPreparing,
    markItemReady
  } = useAdminOrders(restaurant?.id);
  const [activeStation, setActiveStation] = useState<string>('all');
  const [updatingOrders, setUpdatingOrders] = useState<Set<string>>(new Set());
  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set());
  const [toasts, setToasts] = useState<Toast[]>([]);
  const addToast = useCallback((type: 'success' | 'error', message: string) => {
    const id = Date.now();
    setToasts(prev => [...prev, {
      id,
      type,
      message
    }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }, []);
  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  const userRole = tenantCtx?.role || 'owner';
  const isGlobalRole = GLOBAL_ROLES.includes(userRole);
  const isGlobalStation = (code: string) => code === 'all' || code === 'caja';
  const isLockedToProduction = lockedStation != null && !isGlobalStation(lockedStation);
  useEffect(() => {
    if (lockedStation) {
      setActiveStation(lockedStation === 'caja' ? 'all' : lockedStation);
    }
  }, [lockedStation]);
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-955">
        <div className="w-16 h-16 rounded-full bg-[#141414] animate-pulse" data-id="element-956" />
      </div>;
  }
  // Show active orders (not delivered/cancelled)
  const activeOrders = orders.filter((o: any) => !['delivered', 'cancelled', 'out_for_delivery'].includes(o.status));
  const displayOrders = activeOrders.filter((order: any) => {
    if (isGlobalStation(activeStation)) return true;
    // Station views (cocina, pizzeria, bebidas) only see orders that have been
    // accepted by caja — pending/new orders are hidden from production stations
    if (order.status === 'new') return false;
    return order.items.some((item: any) => item.station_code === activeStation);
  });
  const getWaitTimeColor = (createdAt: string) => {
    const minutes = Math.floor((new Date().getTime() - new Date(createdAt).getTime()) / 60000);
    if (minutes > 20) return 'text-red-500 bg-red-50 border-red-200';
    if (minutes > 10) return 'text-amber-500 bg-amber-50 border-amber-200';
    return 'text-emerald-500 bg-emerald-50 border-emerald-200';
  };
  // ─── Order-level action handler ────────────────────────────
  const handleOrderAction = async (orderId: string, action: string) => {
    setUpdatingOrders(prev => new Set(prev).add(orderId));
    let result: ActionResult = {
      success: false,
      error: 'Acción desconocida'
    };
    try {
      switch (action) {
        case 'accept':
          result = await acceptOrder(orderId);
          if (result.success) addToast('success', '✓ Pedido aceptado');
          break;
        case 'start_preparation':
          result = await startOrderPreparation(orderId);
          if (result.success) addToast('success', '🔥 Enviado a cocina');
          break;
        case 'mark_ready':
          result = await markOrderReady(orderId);
          if (result.success) addToast('success', '✅ Pedido listo');
          break;
        case 'deliver':
          result = await deliverOrder(orderId);
          if (result.success) addToast('success', '🚀 Entregado');
          break;
        case 'cancel':
          if (confirm('¿Estás seguro de cancelar este pedido?')) {
            result = await cancelOrder(orderId);
            if (result.success) addToast('success', '❌ Cancelado');
          } else {
            result = {
              success: true
            };
          }
          break;
      }
      if (!result.success && result.error) {
        addToast('error', result.error);
      }
    } catch (err: any) {
      addToast('error', err?.message || 'Error inesperado');
    } finally {
      setUpdatingOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };
  // ─── Item-level action handler ─────────────────────────────
  const handleItemAction = async (itemId: string, currentStatus: string) => {
    setUpdatingItems(prev => new Set(prev).add(itemId));
    try {
      let result: ActionResult;
      if (currentStatus === 'pending') {
        result = await markItemPreparing(itemId);
        if (result.success) addToast('success', '🍳 Item en preparación');
      } else if (currentStatus === 'preparing') {
        result = await markItemReady(itemId);
        if (result.success) addToast('success', '✅ Item listo');
      } else {
        result = {
          success: true
        };
      }
      if (!result!.success && result!.error) {
        addToast('error', result!.error);
      }
    } catch (err: any) {
      addToast('error', err?.message || 'Error al actualizar item');
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };
  // Header title
  let headerTitle = 'KDS — Todas las Estaciones';
  if (activeStation !== 'all') {
    if (isGlobalStation(activeStation)) {
      headerTitle = 'KDS — Caja (Vista Global)';
    } else {
      const stationName = stations.find(s => s.code === activeStation)?.name;
      if (stationName) headerTitle = `KDS — ${stationName}`;
    }
  }
  const getPaymentIcon = (method: string) => {
    switch (method) {
      case 'sinpe':
        return <Smartphone className="w-4 h-4" data-id="element-957" />;
      case 'card':
        return <CreditCard className="w-4 h-4" data-id="element-958" />;
      case 'cash':
        return <Banknote className="w-4 h-4" data-id="element-959" />;
      default:
        return null;
    }
  };
  const getPaymentLabel = (method: string) => {
    switch (method) {
      case 'sinpe':
        return 'SINPE';
      case 'card':
        return 'Tarjeta';
      case 'cash':
        return 'Efectivo';
      default:
        return method;
    }
  };
  const getOrderStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <Badge variant="info" className="bg-blue-50 text-blue-700 border-blue-200" data-id="element-960">
            Nuevo
          </Badge>;
      case 'accepted':
        return <Badge variant="default" className="bg-purple-50 text-purple-700 border-purple-200" data-id="element-961">
            Aceptado
          </Badge>;
      case 'preparing':
        return <Badge variant="default" className="bg-amber-50 text-amber-700 border-amber-200" data-id="element-962">
            Preparando
          </Badge>;
      case 'ready':
        return <Badge variant="success" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-id="element-963">
            Listo
          </Badge>;
      default:
        return <Badge variant="default" data-id="element-964">{status}</Badge>;
    }
  };
  const getItemStatusInfo = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          label: 'Pendiente',
          color: 'bg-gray-100 text-gray-600',
          actionLabel: 'En preparación',
          actionColor: 'bg-amber-500 hover:bg-amber-600 text-white border-amber-600'
        };
      case 'preparing':
        return {
          label: 'Preparando',
          color: 'bg-amber-100 text-amber-700',
          actionLabel: 'Marcar listo',
          actionColor: 'bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-600'
        };
      case 'ready':
        return {
          label: 'Listo',
          color: 'bg-emerald-100 text-emerald-700',
          actionLabel: '',
          actionColor: ''
        };
      default:
        return {
          label: status,
          color: 'bg-gray-100 text-gray-600',
          actionLabel: '',
          actionColor: ''
        };
    }
  };
  // Helper to format short order number
  const getShortOrderNumber = (orderNumber: string) => {
    const parts = orderNumber.split('-');
    return parts[parts.length - 1] || orderNumber;
  };
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-965">
      <div className="min-h-screen bg-[#0B0B0B] p-2 sm:p-3 text-white" data-id="element-966">
        {/* Header — ultra compact */}
        <div className="flex items-center justify-between gap-3 mb-3" data-id="element-967">
          <div className="flex items-center gap-2" data-id="element-968">
            <h1 className="text-lg font-black text-white tracking-tight" data-id="element-969">
              {headerTitle}
            </h1>
            <span className="bg-white/10 text-white/60 px-2 py-0.5 rounded-full text-xs font-bold tabular-nums" data-id="element-970">
              {displayOrders.length}
            </span>
          </div>

          {/* Station Tabs — compact pills */}
          {!isLockedToProduction && <div className="flex overflow-x-auto no-scrollbar gap-1.5" data-id="element-971">
              <button onClick={() => setActiveStation('all')} className={cn('h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap transition-colors active:scale-95', activeStation === 'all' ? 'bg-white text-black' : 'bg-white/5 text-white/50 hover:bg-white/10')} data-id="element-972">
                Todos
              </button>
              {stations.map(station => <button key={station.id} onClick={() => setActiveStation(station.code)} className={cn('h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap transition-colors active:scale-95', activeStation === station.code ? 'bg-white text-black' : 'bg-white/5 text-white/50 hover:bg-white/10')} data-id="element-973">
                  {station.name}
                </button>)}
            </div>}
        </div>

        {/* Orders Grid — high density: 2→3→4 columns */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2 items-start" data-id="element-974">
          {ordersLoading || stationsLoading ? [1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-48 rounded-xl bg-[#141414] animate-pulse border border-white/5" data-id="element-975" />) : displayOrders.length > 0 ? displayOrders.map((order: any) => {
          const visibleItems = order.items.filter((item: any) => isGlobalStation(activeStation) || item.station_code === activeStation);
          const allItemsReady = visibleItems.length > 0 && visibleItems.every((i: any) => i.status === 'ready' || i.status === 'served');
          const isUpdatingOrder = updatingOrders.has(order.id);
          const waitMinutes = Math.floor((new Date().getTime() - new Date(order.created_at).getTime()) / 60000);
          let borderColor = 'border-white/10';
          let cardGlow = '';
          if (order.status === 'new') {
            borderColor = 'border-blue-500';
            cardGlow = 'kds-new-order';
          } else if (order.status === 'accepted') {
            borderColor = 'border-purple-500';
          } else if (order.status === 'preparing') {
            borderColor = 'border-amber-500';
          } else if (allItemsReady) {
            borderColor = 'border-emerald-500';
          }
          return <div key={order.id} className={cn('bg-[#141414] rounded-xl flex flex-col border-l-4 border-y border-r border-white/5 overflow-hidden', borderColor, cardGlow)} data-id="element-976">
                  {/* Header — single dense row */}
                  <div className="px-2.5 py-2 border-b border-white/5 flex items-center justify-between gap-1" data-id="element-977">
                    <div className="flex items-center gap-1.5 min-w-0" data-id="element-978">
                      <span className="text-sm font-black text-white tabular-nums" data-id="element-979">
                        #{getShortOrderNumber(order.order_number)}
                      </span>
                      {order.delivery_type === 'delivery' ? <span className="bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded text-[10px] font-bold" data-id="element-980">
                          🛵
                        </span> : order.delivery_type === 'pickup' ? <span className="bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded text-[10px] font-bold" data-id="element-981">
                          🏠
                        </span> : <span className="bg-white/10 text-white/50 px-1.5 py-0.5 rounded text-[10px] font-bold" data-id="element-982">
                          🍽️{order.table_id || ''}
                        </span>}
                      <span className="text-white/30" data-id="element-983">
                        {getPaymentIcon(order.payment_method)}
                      </span>
                    </div>
                    <span className={cn('text-lg font-black tabular-nums tracking-tighter shrink-0', waitMinutes >= 20 ? 'kds-late-timer' : waitMinutes >= 10 ? 'text-amber-500' : 'text-emerald-500')} data-id="element-984">
                      {waitMinutes}m
                    </span>
                  </div>

                  {/* Customer — tiny */}
                  <div className="px-2.5 py-1 border-b border-white/5" data-id="element-985">
                    <p className="text-xs text-white/50 font-medium truncate" data-id="element-986">
                      {order.customer_name}
                    </p>
                  </div>

                  {/* Notes */}
                  {order.notes && <div className="px-2.5 py-1.5 bg-red-500/10 border-b border-red-500/20" data-id="element-987">
                      <p className="text-[11px] text-red-400 font-bold leading-tight truncate" data-id="element-988">
                        ⚠ {order.notes}
                      </p>
                    </div>}

                  {/* Items — compact checklist */}
                  <div className="flex-1 overflow-y-auto max-h-[200px]" data-id="element-989">
                    {visibleItems.map((item: any) => {
                const isItemReady = item.status === 'ready' || item.status === 'served';
                const isItemPreparing = item.status === 'preparing';
                const isUpdatingItem = updatingItems.has(item.id);
                return <div key={item.id} onClick={() => {
                  if (isUpdatingItem || isItemReady) return;
                  handleItemAction(item.id, item.status);
                }} className={cn('flex items-center gap-2 px-2.5 py-1.5 border-b border-white/5 cursor-pointer select-none transition-all', isItemReady ? 'opacity-30' : isItemPreparing ? 'bg-amber-500/5' : 'hover:bg-white/5 active:scale-[0.98]')} data-id="element-990">
                          {/* Qty */}
                          <span className={cn('w-6 h-6 rounded-full flex items-center justify-center text-xs font-black shrink-0', isItemReady ? 'bg-emerald-500/20 text-emerald-400' : isItemPreparing ? 'bg-amber-500 text-black' : 'bg-white/15 text-white')} data-id="element-991">
                            {item.quantity}
                          </span>

                          {/* Name + options inline */}
                          <div className="flex-1 min-w-0" data-id="element-992">
                            <p className={cn('text-sm font-bold leading-tight truncate', isItemReady ? 'line-through text-white/40' : 'text-white')} data-id="element-993">
                              {item.item_name_snapshot}
                            </p>
                            {item.options && item.options.length > 0 && <p className="text-[10px] text-white/40 truncate leading-tight" data-id="element-994">
                                {item.options.map((o: any) => o.option_name).join(' · ')}
                              </p>}
                            {item.notes && <p className="text-[10px] font-black text-red-400 uppercase truncate" data-id="element-995">
                                ** {item.notes}
                              </p>}
                          </div>

                          {/* Status indicator */}
                          <div className="shrink-0 w-6 flex items-center justify-center" data-id="element-996">
                            {isUpdatingItem ? <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" data-id="element-997" /> : isItemReady ? <Check className="w-4 h-4 text-emerald-500" data-id="element-998" /> : isItemPreparing ? <Check className="w-5 h-5 text-emerald-400" data-id="element-999" /> : <Play className="w-4 h-4 text-amber-400" data-id="element-1000" />}
                          </div>
                        </div>;
              })}
                  </div>

                  {/* Action — compact but tappable */}
                  <div className="p-2 border-t border-white/5" data-id="element-1001">
                    {isGlobalRole ? <>
                        {order.status === 'new' && <button onClick={() => handleOrderAction(order.id, 'accept')} disabled={isUpdatingOrder} className="w-full h-10 rounded-lg text-sm font-black bg-blue-600 hover:bg-blue-500 text-white flex items-center justify-center gap-1 active:scale-95 transition-transform disabled:opacity-50" data-id="element-1002">
                            {isUpdatingOrder ? '...' : '✓ ACEPTAR'}
                          </button>}
                        {order.status === 'accepted' && <button onClick={() => handleOrderAction(order.id, 'start_preparation')} disabled={isUpdatingOrder} className="w-full h-10 rounded-lg text-sm font-black bg-amber-500 hover:bg-amber-400 text-black flex items-center justify-center gap-1 active:scale-95 transition-transform disabled:opacity-50" data-id="element-1003">
                            {isUpdatingOrder ? '...' : '🔥 COCINAR'}
                          </button>}
                        {order.status === 'preparing' && <button onClick={() => handleOrderAction(order.id, 'mark_ready')} disabled={isUpdatingOrder || !allItemsReady} className={cn('w-full h-10 rounded-lg text-sm font-black flex items-center justify-center gap-1 transition-all', allItemsReady ? 'bg-emerald-500 hover:bg-emerald-400 text-black active:scale-95' : 'bg-white/5 text-white/20 cursor-not-allowed')} data-id="element-1004">
                            {isUpdatingOrder ? '...' : allItemsReady ? '✅ LISTO' : 'FALTAN...'}
                          </button>}
                        {order.status === 'ready' && <button onClick={() => handleOrderAction(order.id, 'deliver')} disabled={isUpdatingOrder} className="w-full h-10 rounded-lg text-sm font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-1 active:scale-95 transition-transform disabled:opacity-50" data-id="element-1005">
                            {isUpdatingOrder ? '...' : '🚀 ENTREGAR'}
                          </button>}
                        {['new', 'accepted', 'preparing'].includes(order.status) && <button onClick={() => handleOrderAction(order.id, 'cancel')} disabled={isUpdatingOrder} className="w-full text-center text-[10px] text-white/15 hover:text-red-500 transition-colors mt-1" data-id="element-1006">
                            cancelar
                          </button>}
                      </> : <div className="h-8 flex items-center justify-center" data-id="element-1007">
                        {allItemsReady ? <p className="text-emerald-500 font-black text-xs" data-id="element-1008">
                            ✅ LISTOS
                          </p> : <p className="text-white/30 text-[10px]" data-id="element-1009">
                            Toca items para avanzar
                          </p>}
                      </div>}
                  </div>
                </div>;
        }) : <div className="col-span-full py-20 flex flex-col items-center justify-center text-white/30 bg-[#141414] rounded-xl border border-white/5 border-dashed" data-id="element-1010">
              <ChefHatIcon className="w-16 h-16 text-white/10 mb-4" data-id="element-1011" />
              <h3 className="text-xl font-black text-white/40 mb-1" data-id="element-1012">
                🎉 ¡Todo al día!
              </h3>
              <p className="text-sm" data-id="element-1013">Sin pedidos pendientes.</p>
            </div>}
        </div>

        {/* ═══ TOAST NOTIFICATIONS ═══ */}
        {toasts.length > 0 && <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none" data-id="element-1014">
            {toasts.map(toast => <div key={toast.id} className={cn('pointer-events-auto flex items-center gap-3 px-4 py-2.5 rounded-xl shadow-2xl border text-sm font-bold min-w-[240px] max-w-[360px] anim-slide-up', toast.type === 'success' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-red-600 border-red-500 text-white')} data-id="element-1015">
                <span className="flex-1" data-id="element-1016">{toast.message}</span>
                <button onClick={() => removeToast(toast.id)} className="p-0.5 hover:bg-white/20 rounded transition-colors shrink-0" data-id="element-1017">
                  <X className="w-3.5 h-3.5" data-id="element-1018" />
                </button>
              </div>)}
          </div>}
      </div>
    </AdminLayout>;
}
function ChefHatIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1019">
      <path d="M17 21a1 1 0 0 0 1-1v-5.35c0-.457.316-.844.727-1.041a4 4 0 0 0-2.134-7.589c-.345 0-.682.041-1.005.115-.428-1.833-2.136-3.134-4.088-3.134s-3.66 1.301-4.088 3.134c-.323-.074-.66-.115-1.005-.115a4 4 0 0 0-2.134 7.589c.411.197.727.584.727 1.041V20a1 1 0 0 0 1 1h12z" data-id="element-1020" />
      <path d="M6 17h12" data-id="element-1021" />
    </svg>;
}